import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, Inject, Injectable, makeStateKey, TransferState } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private transferState: TransferState
  ) {
  }

  /**
   * Set an item in storage
   */
  setItem(key: string, value: any): void {
    try {
      // Convert objects to JSON strings
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      
      // Create a state key for this item
      const stateKey = makeStateKey<string>(`storage_${key}`);
      
      // Set the value in transfer state
      this.transferState.set(stateKey, stringValue);
      
      // If in browser, also save to localStorage for persistence
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem(key, stringValue);
      }
    } catch (e) {
      console.error(`Error setting item ${key}:`, e);
    }
  }

  /**
   * Get an item from storage
   */
  getItem(key: string): string | null {
    try {
      // Create a state key for this item
      const stateKey = makeStateKey<string>(`storage_${key}`);
      
      // Check if we have this key in transfer state
      if (this.transferState.hasKey(stateKey)) {
        const value = this.transferState.get(stateKey, null);
        return value;
      }
      
      // If in browser and not in transfer state, try localStorage
      if (isPlatformBrowser(this.platformId)) {
        const value = localStorage.getItem(key);
        
        if (value) {
          // If found in localStorage, also add to transfer state for SSR
          this.transferState.set(stateKey, value);
          return value;
        }
      }

      return null;
    } catch (e) {
      console.error(`Error getting item ${key}:`, e);
      return null;
    }
  }

  /**
   * Remove an item from storage
   */
  removeItem(key: string): void {
    try {
      // Create a state key for this item
      const stateKey = makeStateKey<string>(`storage_${key}`);
      
      // Remove from transfer state
      if (this.transferState.hasKey(stateKey)) {
        // Set to null instead of removing
        this.transferState.set(stateKey, null);
      }
      
      // If in browser, also remove from localStorage
      if (isPlatformBrowser(this.platformId)) {
        localStorage.removeItem(key);
      }

    } catch (e) {
      console.error(`Error removing item ${key}:`, e);
    }
  }
}